import { v4 as uuid } from 'uuid';
import type { ActionDispatcher } from '@de/utils/action-resolver/ActionDispatcher';
import { createServiceTrackedHeaders } from '@de/utils/createServiceTrackedHeaders';
import { newRelicNoticeError } from '@de/utils/newRelicActions';
import { warnOnce } from '@de/utils/warnOnce';
import type { BoundFieldIndex } from './findBoundComponent';
import {
  LEAD_FIELD_NAMES,
  isLeadFieldName,
  validateLeadFields,
  type LeadFieldInput,
  type LeadFieldName,
} from './leadFieldPresets';
import {
  LEAD_SUBMIT_FORM_PARAM,
  isLeadSubmitStateKey,
  leadSubmitFieldErrorKey,
  leadSubmitStateKey,
} from './leadSubmitState';
import {
  LEAD_SUBMIT_VALUE_ROOTS,
  resolveLeadSubmitValues,
} from './resolveLeadSubmitValues';
import {
  LEAD_SUBMIT_V5_ATTRIBUTES_CAP,
  LEAD_SUBMIT_V5_PATH,
  type LeadSubmitV5BffRequest,
  type LeadSubmitV5FieldError,
} from './leadSubmitBffContract';

/** Client timeout for the BFF call; above the BFF's own 8 s upstream timeout. */
export const LEAD_SUBMIT_TIMEOUT_MS = 10_000;

/** The only actions `onSuccess` / `onError` may run. */
export const LEAD_SUBMIT_FOLLOW_UP_TYPES: ReadonlySet<string> = new Set([
  'modal.open',
  'modal.close',
  'formState.set',
  'formState.clear',
  'navigation.goToStep',
  'navigation.goBack',
]);

export const LEAD_SUBMIT_COPY = {
  /** A lead field the server rejected. Raw server messages are never shown. */
  fieldInvalid: {
    firstName: 'Enter a valid first name.',
    lastName: 'Enter a valid last name.',
    email: 'Enter a valid email.',
    phoneNumber: 'Enter a valid phone number.',
  } satisfies Record<LeadFieldName, string>,
  formInvalid: 'Check your information and try again.',
  formUnavailable: "We couldn't send your request. Please try again later.",
  formUncertain:
    'Something went wrong. Your request may have been received, so please wait a moment before trying again.',
} as const;

export interface LeadSubmitEvent {
  readonly formName?: string;
  /** Lead keys sent, in `LEAD_FIELD_NAMES` order. */
  readonly leadFields: readonly LeadFieldName[];
}

/** Host wiring for `lead.submit`. */
export interface LeadSubmitDeps {
  readonly getFieldIndex: () => BoundFieldIndex;
  /** `context.params`, read when a follow-up runs. */
  readonly getContextParams: () => unknown;
  readonly getTrackingContext?: () => Record<string, unknown> | undefined;
  /** Called once per accepted submit, before `onSuccess`. */
  readonly onFormSubmitted?: (event: LeadSubmitEvent) => void;
  /** Prefix for the BFF path; same-origin when absent. */
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
  /**
   * Host-only switch for Admin Preview. Only `true` enables it: the handler
   * validates, writes the status keys and runs `onSuccess` as usual, but
   * makes no request and emits no `FormSubmitted`. Absent means live. Never
   * read from authored params.
   */
  readonly dryRun?: boolean;
  /**
   * Per-scope fingerprint of the last accepted submit. Pass a map the host
   * keeps so a dispatcher rebuild still ignores an unchanged repeat.
   */
  readonly submissions?: Map<string, string>;
}

export interface LeadSubmitRuntime {
  readonly setFormState: (entries: Readonly<Record<string, string>>) => void;
  readonly readFormState: () => Readonly<Record<string, unknown>> | undefined;
  readonly getDispatcher: () => ActionDispatcher;
}

interface FollowUp {
  readonly type: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

type OutcomeReason =
  | 'operator_error'
  | 'upstream_error'
  | 'http-error'
  | 'unexpected-success-body'
  | 'timeout'
  | 'network-error';

type Outcome =
  | { readonly kind: 'success' }
  | {
      readonly kind: 'fieldErrors';
      readonly fieldErrors: readonly LeadSubmitV5FieldError[];
    }
  | {
      readonly kind: 'unavailable';
      readonly reason: OutcomeReason;
      readonly status?: number;
    }
  | {
      readonly kind: 'uncertain';
      readonly reason: OutcomeReason;
      readonly status?: number;
    };

const WARN_NAMESPACE = 'lead-submit-handler';
const UNSCOPED = '';

/**
 * `lead.submit`: validate the enclosing form's lead fields, POST once to the
 * v5 BFF, and write the `leadSubmit__*` status, busy and error keys. Params
 * arrive resolved from `resolveActions`, except `onSuccess` / `onError`, which
 * are resolved here when they run. Never rejects.
 */
export function createLeadSubmitHandler(
  deps: LeadSubmitDeps,
  runtime: LeadSubmitRuntime,
): (params?: Readonly<Record<string, unknown>>) => Promise<void> | void {
  const inFlight = new Set<string>();
  const submissions = deps.submissions ?? new Map<string, string>();

  return (params) => {
    const formName = stringValue(params?.[LEAD_SUBMIT_FORM_PARAM]);
    const scope = formName ?? UNSCOPED;
    const statusKey = leadSubmitStateKey('status', formName);
    const busyKey = leadSubmitStateKey('busy', formName);
    const errorKey = leadSubmitStateKey('error', formName);
    const formState = runtime.readFormState() ?? {};

    if (inFlight.has(scope) || formState[statusKey] === 'submitting') return;

    const form = formName ? deps.getFieldIndex().forms[formName] : undefined;
    const fields: LeadFieldInput[] = [];
    for (const [leadField, info] of Object.entries(form?.leadFields ?? {})) {
      if (!isLeadFieldName(leadField)) continue;
      fields.push({
        formKey: info.formKey,
        leadField,
        value: stringOf(formState[info.formKey]),
        required: info.required === true,
      });
    }

    const organizationId = stringValue(params?.organizationId);
    const offeringId = stringValue(params?.offeringId);
    const conversionId = stringValue(params?.conversionId);
    const paramsLead = readLeadParam(params?.lead);
    const attributes = readStringRecord(params?.attributes);

    const dryRun = deps.dryRun === true;
    const fingerprint = JSON.stringify([
      dryRun,
      Object.keys(form?.fields ?? {}).map((key) => [
        key,
        stringOf(formState[key]),
      ]),
      organizationId,
      offeringId,
      conversionId,
      paramsLead,
      attributes,
    ]);
    if (
      formState[statusKey] === 'success' &&
      submissions.get(scope) === fingerprint
    ) {
      return;
    }

    const onSuccess = readFollowUps(params?.onSuccess, 'onSuccess');
    const onError = readFollowUps(params?.onError, 'onError');
    const runFollowUps = (followUps: readonly FollowUp[]) =>
      dispatchFollowUps(followUps, deps, runtime);

    const validation = validateLeadFields({ fields, paramsLead, formName });
    if (!validation.valid) {
      runtime.setFormState({
        ...validation.errors,
        [statusKey]: 'error',
        [busyKey]: 'false',
      });
      return;
    }

    const request = buildRequest({
      organizationId,
      offeringId,
      conversionId,
      lead: validation.lead,
      attributes,
      trackingContext: readTrackingContext(deps),
    });
    if ('refused' in request) {
      if (!dryRun && !request.correctable) {
        reportFailure({ reason: request.refused });
      }
      runtime.setFormState({
        ...validation.errors,
        [errorKey]: request.correctable
          ? LEAD_SUBMIT_COPY.formInvalid
          : LEAD_SUBMIT_COPY.formUnavailable,
        [statusKey]: 'error',
        [busyKey]: 'false',
      });
      return;
    }

    inFlight.add(scope);
    runtime.setFormState({
      ...validation.errors,
      [statusKey]: 'submitting',
      [busyKey]: 'true',
    });

    const sent: Promise<Outcome> = dryRun
      ? Promise.resolve({ kind: 'success' })
      : postOnce(request.body, deps);

    // An unmount does not abort the request: its outcome still settles the
    // state keys, and follow-ups run on the dispatcher that started it.
    return sent.then((outcome) => {
      let terminal: Record<string, string> = {
        [errorKey]: LEAD_SUBMIT_COPY.formUncertain,
        [statusKey]: 'error',
        [busyKey]: 'false',
      };
      try {
        if (outcome.kind === 'success') {
          submissions.set(scope, fingerprint);
          terminal = {
            [statusKey]: 'success',
            [busyKey]: 'false',
            [errorKey]: '',
          };
        } else {
          submissions.delete(scope);
          terminal = {
            ...errorStateFor(outcome, fields, formName),
            [statusKey]: 'error',
            [busyKey]: 'false',
          };
        }
      } finally {
        // Always leave `submitting`; the guard would ignore every later click.
        inFlight.delete(scope);
        runtime.setFormState(terminal);
      }

      if (outcome.kind === 'success') {
        if (!dryRun) {
          try {
            deps.onFormSubmitted?.({
              formName,
              leadFields: LEAD_FIELD_NAMES.filter(
                (leadField) => validation.lead[leadField] !== undefined,
              ),
            });
          } catch (err) {
            reportFailure({ reason: 'form-submitted-event-threw' }, err);
          }
        }
        runFollowUps(onSuccess);
        return;
      }
      runFollowUps(onError);
    });
  };
}

function errorStateFor(
  outcome: Exclude<Outcome, { kind: 'success' }>,
  fields: readonly LeadFieldInput[],
  formName: string | undefined,
): Record<string, string> {
  if (outcome.kind === 'fieldErrors') {
    return mapFieldErrors(outcome.fieldErrors, fields, formName);
  }
  // The BFF logs or notices each operator_error with the LDS status it hides.
  if (outcome.reason === 'operator_error') {
    const http = outcome.status !== undefined ? `, HTTP ${outcome.status}` : '';
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] The lead service refused the submission (operator_error${http}); the BFF logs have the LDS status`,
    );
  } else {
    reportFailure(outcome);
  }
  return {
    [leadSubmitStateKey('error', formName)]:
      outcome.kind === 'unavailable'
        ? LEAD_SUBMIT_COPY.formUnavailable
        : LEAD_SUBMIT_COPY.formUncertain,
  };
}

/**
 * Server-rejected lead fields go to their bound input's error key; anything
 * else (a `params.lead` value, `attributes.<key>`, no detail) to the form error.
 */
function mapFieldErrors(
  fieldErrors: readonly LeadSubmitV5FieldError[],
  fields: readonly LeadFieldInput[],
  formName: string | undefined,
): Record<string, string> {
  const errors: Record<string, string> = {};
  let unmatched = fieldErrors.length === 0;
  for (const { field } of fieldErrors) {
    const bound = fields.find(({ leadField }) => leadField === field);
    if (bound) {
      errors[leadSubmitFieldErrorKey(bound.formKey, formName)] =
        LEAD_SUBMIT_COPY.fieldInvalid[bound.leadField];
    } else {
      unmatched = true;
    }
  }
  errors[leadSubmitStateKey('error', formName)] = unmatched
    ? LEAD_SUBMIT_COPY.formInvalid
    : '';
  return errors;
}

function buildRequest(parts: {
  readonly organizationId?: string;
  readonly offeringId?: string;
  readonly conversionId?: string;
  readonly lead: Readonly<Partial<Record<LeadFieldName, string>>>;
  readonly attributes: Readonly<Record<string, string>>;
  readonly trackingContext: Record<string, unknown>;
}):
  | { readonly body: LeadSubmitV5BffRequest }
  | { readonly refused: string; readonly correctable: boolean } {
  const { organizationId, offeringId, conversionId, attributes } = parts;
  if (!organizationId) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Not sending: organizationId resolved to nothing. A {{context.params.*}} id is empty when the host provides no page params, as in Admin Preview`,
    );
    return { refused: 'missing-routing-id', correctable: false };
  }
  const overCap = attributesOverCap(attributes);
  if (overCap) return overCap;
  return {
    body: {
      organizationId,
      ...(offeringId ? { offeringId } : {}),
      ...(conversionId ? { conversionId } : {}),
      // A lead missing a required key is still sent; the BFF's 400 maps like any field error.
      lead: parts.lead as LeadSubmitV5BffRequest['lead'],
      ...(Object.keys(attributes).length > 0
        ? { attributes: { ...attributes } }
        : {}),
      trackingContext: parts.trackingContext,
    },
  };
}

/**
 * Too many keys is an authoring error. A value that is too long is usually
 * text the visitor typed, so they can shorten it.
 */
function attributesOverCap(
  attributes: Readonly<Record<string, string>>,
): { readonly refused: string; readonly correctable: boolean } | undefined {
  const { maxKeys, maxKeyLength, maxValueLength } =
    LEAD_SUBMIT_V5_ATTRIBUTES_CAP;
  const keys = Object.keys(attributes);
  if (keys.length > maxKeys) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Not sending: ${keys.length} attributes is above the ${maxKeys}-key cap`,
    );
    return { refused: 'attributes-over-cap', correctable: false };
  }
  const longKeys = keys.filter((key) => key.length > maxKeyLength);
  if (longKeys.length > 0) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Not sending: ${longKeys.length} attribute keys are longer than ${maxKeyLength} characters`,
    );
    return { refused: 'attribute-key-too-long', correctable: false };
  }
  const oversized = keys.filter(
    (key) => attributes[key].length > maxValueLength,
  );
  if (oversized.length > 0) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Not sending: attributes ${oversized.join(', ')} are longer than ${maxValueLength} characters`,
    );
    return { refused: 'attribute-too-long', correctable: true };
  }
  return undefined;
}

async function postOnce(
  body: LeadSubmitV5BffRequest,
  deps: LeadSubmitDeps,
): Promise<Outcome> {
  const doFetch = deps.fetch ?? globalThis.fetch;
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    deps.timeoutMs ?? LEAD_SUBMIT_TIMEOUT_MS,
  );
  let response: Response;
  try {
    response = await doFetch(`${deps.baseUrl ?? ''}${LEAD_SUBMIT_V5_PATH}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...createServiceTrackedHeaders(uuid()),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timer);
    return {
      kind: 'uncertain',
      reason: controller.signal.aborted ? 'timeout' : 'network-error',
    };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  } finally {
    clearTimeout(timer);
  }
  return readOutcome(response, payload);
}

function readOutcome(response: Response, payload: unknown): Outcome {
  const record = isPlainObject(payload) ? payload : {};
  const { status } = response;
  if (response.ok) {
    return record.status === 'success' &&
      typeof record.submissionId === 'string'
      ? { kind: 'success' }
      : { kind: 'uncertain', reason: 'unexpected-success-body', status };
  }
  switch (record.code) {
    case 'field_validation':
    case 'invalid_request':
      return {
        kind: 'fieldErrors',
        fieldErrors: readFieldErrors(record.fieldErrors),
      };
    case 'operator_error':
      return { kind: 'unavailable', reason: 'operator_error', status };
    case 'upstream_error':
      return { kind: 'uncertain', reason: 'upstream_error', status };
    default:
      return status >= 500
        ? { kind: 'uncertain', reason: 'http-error', status }
        : { kind: 'unavailable', reason: 'http-error', status };
  }
}

function readFieldErrors(value: unknown): LeadSubmitV5FieldError[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is LeadSubmitV5FieldError =>
      isPlainObject(entry) && typeof entry.field === 'string',
  );
}

// Reason and HTTP status only: bodies and lead values never leave the browser here.
function reportFailure(
  outcome: { readonly reason: string; readonly status?: number },
  cause?: unknown,
): void {
  newRelicNoticeError(
    cause instanceof Error ? cause : new Error('Failed to submit v5 lead'),
    {
      source: 'leadSubmit',
      reason: outcome.reason,
      ...(outcome.status !== undefined ? { status: outcome.status } : {}),
    },
  );
}

function readTrackingContext(deps: LeadSubmitDeps): Record<string, unknown> {
  try {
    return deps.getTrackingContext?.() ?? {};
  } catch {
    return {};
  }
}

function readLeadParam(value: unknown): Partial<Record<LeadFieldName, string>> {
  const lead: Partial<Record<LeadFieldName, string>> = {};
  const raw = readStringRecord(value);
  const dropped: string[] = [];
  for (const [key, entry] of Object.entries(raw)) {
    if (isLeadFieldName(key)) lead[key] = entry;
    else dropped.push(key);
  }
  if (dropped.length > 0) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Ignoring params.lead keys outside the v5 lead set: ${dropped.join(', ')}`,
    );
  }
  return lead;
}

function readStringRecord(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isPlainObject(value)) return out;
  for (const [key, entry] of Object.entries(value)) {
    const text = stringValue(entry);
    if (text !== undefined) out[key] = text;
  }
  return out;
}

/** Own parser for the closed follow-up set; a bad entry drops the whole list. */
function readFollowUps(value: unknown, param: string): FollowUp[] {
  if (value === undefined) return [];
  const list = Array.isArray(value) ? value : [value];
  const followUps: FollowUp[] = [];
  for (const entry of list) {
    const type = isPlainObject(entry) ? entry.type : undefined;
    const rawParams = isPlainObject(entry) ? entry.params : undefined;
    if (
      typeof type !== 'string' ||
      !LEAD_SUBMIT_FOLLOW_UP_TYPES.has(type) ||
      (rawParams !== undefined && !isPlainObject(rawParams))
    ) {
      warnOnce(
        WARN_NAMESPACE,
        `[lead.submit] Dropping ${param}: each entry needs a type from ${Array.from(LEAD_SUBMIT_FOLLOW_UP_TYPES).join(', ')} and object params`,
      );
      return [];
    }
    const params = isPlainObject(rawParams) ? rawParams : undefined;
    followUps.push({ type, ...(params ? { params } : {}) });
  }
  if (followUps.length === 0) {
    warnOnce(
      WARN_NAMESPACE,
      `[lead.submit] Dropping ${param}: the list is empty`,
    );
  }
  return followUps;
}

function dispatchFollowUps(
  followUps: readonly FollowUp[],
  deps: LeadSubmitDeps,
  runtime: LeadSubmitRuntime,
): void {
  if (followUps.length === 0) return;
  const dispatcher = runtime.getDispatcher();
  for (const followUp of followUps) {
    if (dispatcher.has?.(followUp.type) === false) {
      warnOnce(
        WARN_NAMESPACE,
        `[lead.submit] Follow-up "${followUp.type}" has no handler; dropping the rest of the list`,
      );
      return;
    }
    const scope = {
      contextParams: deps.getContextParams(),
      formState: (runtime.readFormState() ?? {}) as Record<string, string>,
    };
    const params =
      followUp.params === undefined
        ? undefined
        : guardStateKeys(followUp.type, resolveObject(followUp.params, scope));
    dispatcher.dispatch(followUp.type, params);
  }
}

type ValueScope = Parameters<typeof resolveLeadSubmitValues>[1];

/**
 * Resolves each template leaf against `LEAD_SUBMIT_VALUE_ROOTS`; a refused,
 * unresolved or empty result is dropped. Literals pass through unchanged.
 */
function resolveObject(
  value: Readonly<Record<string, unknown>>,
  scope: ValueScope,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    const resolved = resolveLeaf(entry, scope);
    if (resolved !== undefined) out[key] = resolved;
  }
  return out;
}

function resolveLeaf(value: unknown, scope: ValueScope): unknown {
  if (typeof value === 'string') {
    if (!value.includes('{{')) return value;
    return resolveLeadSubmitValues({ value }, scope, LEAD_SUBMIT_VALUE_ROOTS)
      .value;
  }
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (Array.isArray(value)) {
    return value
      .map((entry) => resolveLeaf(entry, scope))
      .filter((entry) => entry !== undefined);
  }
  if (isPlainObject(value)) return resolveObject(value, scope);
  return undefined;
}

/** `leadSubmit__*` keys belong to the handler; follow-ups cannot write or clear them. */
function guardStateKeys(
  type: string,
  params: Record<string, unknown>,
): Record<string, unknown> {
  if (type === 'formState.set' && isPlainObject(params.values)) {
    const values = Object.fromEntries(
      Object.entries(params.values).filter(
        ([key]) => !isLeadSubmitStateKey(key),
      ),
    );
    return { ...params, values };
  }
  if (type === 'formState.clear' && Array.isArray(params.keys)) {
    const keys = params.keys.filter(
      (key) => typeof key !== 'string' || !isLeadSubmitStateKey(key),
    );
    return { ...params, keys };
  }
  return params;
}

function stringValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function stringOf(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
